<!-- The main People page: one row per person across every group and Tutoring. Tap a row for
     where their balance comes from. -->
<script lang="ts">
  import { formatCents, overviewProgress, overviewStatus, overviewTrend } from "@split/core";
  import { fill, shows } from "../settings/index.ts";
  import * as T from "../settings/people.ts";
  import * as A from "../settings/amounts.ts";
  import PersonCard from "../components/PersonCard.svelte";
  import { joinTrace, overallColor, type CardViewer } from "../lib/personCard.ts";
  import { today } from "../lib/format.ts";
  import { overviewOf, people, peopleTotals, type PeopleRow } from "../lib/people.svelte.ts";
  import { app } from "../lib/store.svelte.ts";
  import { shortDateTime } from "../lib/format.ts";
  import SetupPrompt from "../components/SetupPrompt.svelte";

  $effect(() => {
    void people.load();
  });
  // And again whenever saved changes move on, in any group (the Overview does the same).
  $effect(() => {
    void app.confirmed;
    void app.othersVersion;
    people.refreshSoon();
  });

  let q = $state("");
  let open = $state<string | null>(null);
  const you = $derived(people.scope === "you");
  const rows = $derived(
    (people.rows ?? []).filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase())),
  );
  // The header is the exact sum of the rows below it (you aren't counted). The Overview uses the same.
  const totals = $derived(peopleTotals(people.rows ?? []));
  const owedToYou = $derived(totals.owedToYouCents);
  const youOwe = $derived(totals.youOweCents);

  // ---- one identity per person (the owner): matches are only ever suggestions until you say so
  const byKey = $derived(new Map((people.rows ?? []).map((r) => [r.key, r])));
  const whereOf = (rowKey: string) => byKey.get(rowKey)?.groups.join(", ") || "";
  let mergeTo = $state<Record<string, string>>({});
  async function run(path: "merge" | "split" | "not-same" | "undo", body: Record<string, unknown>, done: string) {
    const err = await people.act(path, body);
    app.toast(err || done, err ? "error" : undefined);
  }
  /** Any one record of a row stands for the whole row when merging. */
  const recOf = (r: PeopleRow | undefined) => r?.lines.find((l) => l.record)?.record;
  function mergeRows(a: PeopleRow, otherKey: string) {
    const b = byKey.get(otherKey);
    const ra = recOf(a);
    const rb = recOf(b);
    if (!ra || !rb || !b) return;
    void run("merge", { records: [ra, rb] }, `${a.name} and ${b.name} are one person now`);
    mergeTo[a.key] = "";
  }

  const viewer: CardViewer = { side: "owner" };
  const self: CardViewer = { side: "self" };
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>People</h1>
      {#if !you}
        <p class="lede">
          {#if owedToYou || youOwe}
            {fill(A.TOTALS_LINE, { owedLabel: A.TOTAL_OWED_LABEL, owed: formatCents(owedToYou), oweLabel: A.TOTAL_OWE_LABEL, owe: formatCents(youOwe) })}
          {:else}
            {A.TOTALS_SQUARE}
          {/if}
        </p>
      {/if}
    </div>
  </div>

  {#if you}<SetupPrompt />{/if}
  {#if people.stale && people.rows}<p class="muted small">Offline: showing the last copy on this device.</p>{/if}
  {#if people.error}<div class="empty">{people.error}</div>
  {:else if !people.rows}<p class="muted">Getting everyone's balances from the server.</p>
  {:else}
    {#if !you && people.suggestions.length}
      <section class="matches" aria-label="Possible matches">
        <h2>Possible matches</h2>
        {#if shows(T.IDENT_SUGGEST_NOTE)}<p class="muted small">{T.IDENT_SUGGEST_NOTE}</p>{/if}
        {#each people.suggestions.slice(0, 12) as s (s.records.join("|"))}
          <div class="match">
            <div class="pair">
              <span><strong>{s.names[0]}</strong> <span class="muted">· {whereOf(s.rows[0])}</span></span>
              <span><strong>{s.names[1]}</strong> <span class="muted">· {whereOf(s.rows[1])}</span></span>
              <span class="muted small">{s.reason === "full-name" ? "Same full name" : "One only has a first name"}</span>
            </div>
            <div class="acts">
              <button class="btn small primary" disabled={people.busy} onclick={() => run("merge", { records: s.records }, `${s.names[1]} is one person now`)}>Same person</button>
              <button class="btn small" disabled={people.busy} onclick={() => run("not-same", { records: s.records }, "Got it: not the same person")}>Not the same</button>
            </div>
          </div>
        {/each}
      </section>
    {/if}
    {#if people.rows.length > 8}
      <div class="ptools">
        <input class="input" type="search" bind:value={q} placeholder="Search people" aria-label="Search people" />
      </div>
    {/if}
    <div class="list" aria-label="People">
      {#each rows as r (r.key)}
        {@const o = overviewOf(r)}
        <PersonCard
          overview={o}
          name={r.name}
          scope="everyone"
          viewer={you ? self : viewer}
          open={open === r.key}
          ontoggle={() => (open = open === r.key ? null : r.key)}
          groups={r.groups}
          isYou={r.isYou && !you}
          color={overallColor(o)}
          status={overviewStatus(o)}
          progress={r.isYou ? null : overviewProgress(o)}
          spark={overviewTrend(o, today())}
          trace={you || !T.SHOW_JOIN_TRACE ? [] : joinTrace(r.lines)}
        >
          {#snippet badges()}
            {#if r.isYou && !you}<span class="badge">{T.BADGE_YOU}</span>{/if}
            {#if r.isYou && !you}{:else if r.hasAccount}<span class="badge ok">{T.BADGE_ACCOUNT}</span>{:else}<span class="badge">{T.BADGE_NO_ACCOUNT}</span>{/if}
          {/snippet}
          {#snippet extra()}
            {#if !you}
              <div class="ident">
                {#if r.identityId && r.lines.filter((l) => l.record).length > 1}
                  {#if shows(T.IDENT_SPLIT_NOTE)}<p class="muted small">{T.IDENT_SPLIT_NOTE}</p>{/if}
                  <div class="splits">
                    {#each r.lines.filter((l) => l.record) as l (l.record)}
                      <button class="btn small" disabled={people.busy} onclick={() => run("split", { record: l.record }, fill(T.IDENT_SPLIT_TOAST, { name: l.recordName ?? "" }))}>{fill(T.IDENT_SPLIT_BUTTON, { name: l.recordName ?? "", label: l.label })}</button>
                    {/each}
                  </div>
                {/if}
                <label class="merge">
                  <span class="muted small">{T.IDENT_SAME_AS}</span>
                  <select class="input" bind:value={mergeTo[r.key]} aria-label={T.IDENT_SAME_AS}>
                    <option value="">{T.IDENT_PICK}</option>
                    {#each (people.rows ?? []).filter((o) => o.key !== r.key && !(o.isYou && r.isYou)) as o (o.key)}
                      <option value={o.key}>{fill(T.IDENT_OPTION, { name: o.name, groups: o.groups.join(", ") || T.IDENT_NO_GROUPS })}</option>
                    {/each}
                  </select>
                  <button class="btn small" disabled={!mergeTo[r.key] || people.busy} onclick={() => mergeRows(r, mergeTo[r.key]!)}>{T.IDENT_MERGE}</button>
                </label>
              </div>
            {/if}
          {/snippet}
        </PersonCard>
      {:else}
        <div class="empty">{q ? "Nobody by that name." : "No one yet."}</div>
      {/each}
    </div>
    {#if !you && people.actions.length}
      <section class="changes" aria-label="Recent changes to who is who">
        <h2>Recent changes to who is who</h2>
        <ul>
          {#each people.actions.slice(0, 8) as a (a.id)}
            <li>
              <span>{a.summary} <span class="muted small">{shortDateTime(a.at)}</span></span>
              {#if a.undone}<span class="muted small">Undone</span>
              {:else}<button class="btn small" disabled={people.busy} onclick={() => run("undo", { actionId: a.id }, "Undone")}>Undo</button>{/if}
            </li>
          {/each}
        </ul>
      </section>
    {/if}
  {/if}
</div>

<style>
  .matches,
  .changes {
    display: grid;
    gap: 8px;
    margin: 0 0 18px;
  }
  .changes {
    margin: 22px 0 0;
  }
  .matches h2,
  .changes h2 {
    margin: 0;
    font-size: var(--t-lg);
  }
  .match {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
  }
  .pair {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .acts,
  .splits {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .ident {
    display: grid;
    gap: 8px;
    padding: 0 16px 14px;
  }
  .merge {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .merge .input {
    max-width: 320px;
  }
  .changes ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 6px;
  }
  .changes li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-size: var(--t-sm);
  }
  .ptools {
    margin-bottom: 12px;
    max-width: 360px;
  }
  .small {
    font-size: var(--t-sm);
  }
</style>
